import {
  addDays, addMonths,
  addYears,
  compareAsc,
  differenceInCalendarMonths,
  differenceInMonths, endOfMonth,
  startOfToday,
  startOfYear
} from "date-fns";
import {
  getDateToday,
  getISODateFromDate, isoToLocalDate,
  numberToMoneyFormat,
  numberToPercentFormat, roundMoney
} from "../../stores/StoreUtilities";
import Person from "../../model/Person";
import {ISocialSecurityBenefit} from "../../model/changes/SocialSecurityStrategyChange";
import {globalColors} from "../../styles/globalStyles";


export type RetirementBenefits = {
  myStart?: Date,
  myBenefit: number,
  myPercent: number,
  myDescription: string
  spousalStart?: Date
  spousalBenefit: number
  spousalPercent: number
  spousalDescription: string
}

class SocialSecurityCalc {
  static getFullRetirementAge(birthDate : Date) : number {
    let age = 67
    let birthYear = birthDate.getFullYear()

    if (birthDate.getMonth() === 0 && birthDate.getDate() === 1) {
      // Handle Jan 1st birth date
      --birthYear
    }

    if (birthYear >= 1960) {
      age = 67
    } else if (birthYear === 1959) {
      age = 66 + 10/12
    } else if (birthYear === 1958) {
      age = 66 + 8/12
    } else if (birthYear === 1957) {
      age = 66 + 6/12
    } else if (birthYear === 1956) {
      age = 66 + 4/12
    } else if (birthYear === 1955) {
      age = 66 + 2/12
    } else if (birthYear >= 1943 && birthYear <= 1954) {
      age = 66
    } else if (birthYear === 1942) {
      age = 65 + 10/12
    } else if (birthYear === 1941) {
      age = 65 + 8/12
    } else if (birthYear === 1940) {
      age = 65 + 6/12
    } else if (birthYear === 1939) {
      age = 65 + 4/12
    } else if (birthYear === 1938) {
      age = 65 + 2/12
    } else {
      age = 65
    }

    return age
  }

  static getDelayedRetirementAnnualPercent(birthDate: Date) {
    let percent = 0
    let birthYear = birthDate.getFullYear()

    if (birthDate.getMonth() === 0 && birthDate.getDate() === 1) {
      // Handle Jan 1st birth date
      --birthYear
    }

    if (birthYear >= 1943) {
      percent = 0.08
    } else if (birthYear >= 1941) {
      percent = 0.075
    } else if (birthYear >= 1939) {
      percent = 0.07
    } else if (birthYear >= 1937) {
      percent = 0.065
    } else if (birthYear >= 1935) {
      percent = 0.06
    } else if (birthYear >= 1933) {
      percent = 0.055
    } else if (birthYear >= 1931) {
      percent = 0.05
    } else if (birthYear >= 1929) {
      percent = 0.045
    } else if (birthYear >= 1927) {
      percent = 0.04
    } else if (birthYear >= 1925) {
      percent = 0.035
    } else {
      percent = 0.03
    }

    return percent
  }

  /* Rules for social security benefits
    Personal Benefit
    Age >= 62 && Age < FRA:  FRB - (1% * 5/9 for first 36 mon) - (1% * 5/12 for remaining months)
    FRA: FRB & 100%
    Age > FRA:  FRB + (Delayed % * numMonths)
   */

  static calculateRetirementBenefits(birthDate: Date, startDate: Date, fullBenefit: number,
                                     spouseBirthDate?: Date, spouseStartDate?: Date, spouseFullBenefit?: number) : RetirementBenefits {
    const benefits: RetirementBenefits = {
      myStart: undefined,
      myBenefit: 0,
      myPercent: 0,
      myDescription: "",
      spousalStart: undefined,
      spousalBenefit: 0,
      spousalPercent: 0,
      spousalDescription: ""
    }

    let fullRetirementAgeInMonths = Math.round(this.getFullRetirementAge(birthDate) * 12)
    const birthDay = birthDate.getDate()
    if (birthDay === 1) {
      --fullRetirementAgeInMonths
    }
    let retirementAgeInMonths = Math.max(Math.min(differenceInMonths(startDate, birthDate), 70 * 12), 62 * 12)

    if (!fullBenefit) {
      benefits.myStart = startDate
      benefits.myDescription = "No Retirement Benefit"
    } else if (retirementAgeInMonths < fullRetirementAgeInMonths) {
      // Calculate reduced benefit
      let percentReduction = 0
      let numMonths = fullRetirementAgeInMonths - retirementAgeInMonths
      if (numMonths <= 36) {
        percentReduction = numMonths * (0.01 * 5/9)
      } else {
        percentReduction = (36 * (0.01 * 5/9)) + ((numMonths - 36) * (0.01 * 5/12))
      }
      benefits.myStart = startDate
      benefits.myBenefit = Math.round(fullBenefit + (fullBenefit * -percentReduction))
      benefits.myPercent = 1 - percentReduction
      benefits.myDescription = `${numberToPercentFormat(benefits.myPercent, 0)} Retirement Benefit`
    } else if (retirementAgeInMonths > fullRetirementAgeInMonths) {
      // Calculate increased benefit
      const delayedPercent = this.getDelayedRetirementAnnualPercent(birthDate)
      const numMonths = retirementAgeInMonths - fullRetirementAgeInMonths
      const percentIncrease = numMonths * (delayedPercent / 12)
      benefits.myBenefit = Math.round(fullBenefit + (fullBenefit * (percentIncrease)))
      benefits.myStart = startDate
      benefits.myPercent = 1 + percentIncrease
      benefits.myDescription = `${numberToPercentFormat(benefits.myPercent, 0)} Retirement Benefit`
    } else { // Full benefit
      benefits.myBenefit = fullBenefit
      benefits.myStart = startDate
      benefits.myPercent = 1
      benefits.myDescription = `${numberToPercentFormat(benefits.myPercent, 0)} Retirement Benefit`
    }

    if (spouseBirthDate && spouseFullBenefit && spouseStartDate) {
      // The excess spousal benefit is based on PIAs, not reduced/increased benefits
      const excessBenefit = Math.round(spouseFullBenefit / 2 - fullBenefit)
      if (excessBenefit > 0) {
        // Spousal benefits cannot begin before the primary earner files: they start at
        // the latter of this person's or their spouse's start date, and never before
        // this person is 62
        let spousalStartDate = compareAsc(startDate, spouseStartDate) >= 0 ? startDate : spouseStartDate
        const earliestSpousalDate = addMonths(birthDate, 62 * 12)
        if (compareAsc(spousalStartDate, earliestSpousalDate) < 0) {
          spousalStartDate = earliestSpousalDate
        }

        // Early reduction is based on this person's age when the spousal benefit actually
        // starts; starting at or after FRA pays the full excess. No delayed retirement
        // credits apply to spousal benefits.
        const spousalAgeInMonths = Math.min(differenceInMonths(spousalStartDate, birthDate), 70 * 12)
        let spousalPercent = 1
        if (spousalAgeInMonths < fullRetirementAgeInMonths) {
          const numMonths = fullRetirementAgeInMonths - spousalAgeInMonths
          if (numMonths <= 36) {
            spousalPercent = 1 - (numMonths * (0.01 * 25/36))
          } else {
            spousalPercent = 1 - ((36 * (0.01 * 25/36)) + ((numMonths - 36) * (0.01 * 5/12)))
          }
        }

        // Total paid while the spousal benefit is in effect (own benefit plus reduced
        // excess), capped at 50% of the spouse's PIA
        const spousalBenefit = Math.round(Math.min(Math.round(excessBenefit * spousalPercent) + benefits.myBenefit, spouseFullBenefit / 2))

        if (spousalBenefit > benefits.myBenefit) {
          benefits.spousalStart = spousalStartDate
          benefits.spousalBenefit = spousalBenefit
          benefits.spousalPercent = spousalPercent
          benefits.spousalDescription = `${numberToPercentFormat(spousalPercent, 0)} Spousal Benefit`
          if (!fullBenefit || compareAsc(spousalStartDate, startDate) <= 0) {
            // Nothing is payable before the spousal benefit begins, so there is no
            // separate personal benefit period
            benefits.myStart = undefined
          }
        }
      }
    }

    return benefits
  }

  static maximizeRetirementBenefits(persons: Person[], fullBenefit: number, infRate: number, spouseFullBenefit?: number,
                                    netWorthCalc?: (benefits: ISocialSecurityBenefit[]) => number) : ISocialSecurityBenefit[] {
    let benefits: ISocialSecurityBenefit[] = []
    let maxLifetimeBenefits = 0

    const birthDate = isoToLocalDate(persons[0].birthDate)
    const birthDate2 = persons.length > 1 ? isoToLocalDate(persons[1].birthDate) : undefined

    let monthRange1 = [62*12, 70*12]
    let monthRange2 = [62*12, 70*12]
    let monthIncr = 1
    let maxMonth1 = 62*12
    let maxMonth2 = 62*12
    let passCount = 1

    // Limit lower month range to current age
    const today = startOfToday()
    const monthAge1 = Math.trunc(differenceInMonths(today, birthDate) / 12) * 12
    if (monthRange1[0] < monthAge1) {
      monthRange1[0] = monthAge1
    }
    if (birthDate2) {
      const monthAge2 = Math.trunc(differenceInMonths(today, birthDate2) / 12) * 12

      if (monthRange2[0] < monthAge2) {
        monthRange2[0] = monthAge2
      }
    }

    if (netWorthCalc) {
      monthIncr = 12
      passCount = 3
    }

    for (let pass = 0; pass < passCount; pass++) {
      let count = 0
      // console.debug(`Pass ${pass}`)
      for (let month = monthRange1[0]; month <= monthRange1[1]; month += monthIncr) {
        let startDate = addMonths(birthDate, month)
        let year = Math.trunc(month / 12)
        if (birthDate2) {
          // When person 2 has no work record, any start before person 1 files is a
          // duplicate of starting when person 1 files (the spousal benefit defers), so
          // skip duplicate grid points. Keep the pruned floor aligned to the monthIncr
          // grid so the coarse and fine refinement passes sample the same date sets.
          // The floor is a per-iteration local; monthRange2 must not be mutated because
          // it is reused across refinement passes.
          let month2Start = monthRange2[0]
          if (!spouseFullBenefit && fullBenefit) {
            const monthsAtStart1 = Math.min(differenceInMonths(startDate, birthDate2), 70 * 12)
            if (monthsAtStart1 > month2Start) {
              month2Start = Math.min(month2Start + Math.ceil((monthsAtStart1 - month2Start) / monthIncr) * monthIncr, monthRange2[1])
            }
          }
          for (let month2 = month2Start; month2 <= monthRange2[1]; month2 += monthIncr) {
            let startDate2 = addMonths(birthDate2, month2)
            let year2 = Math.trunc(month2 / 12)
            // console.debug(`(${year}-${month}mo) / (${year2}-${month2}mo)`)
            const benefit1 = this.calculateRetirementBenefits(birthDate, startDate, fullBenefit, birthDate2, startDate2, spouseFullBenefit)
            const benefit2 = this.calculateRetirementBenefits(birthDate2, startDate2, spouseFullBenefit ?? 0, birthDate, startDate, fullBenefit)
            const ssBenefits: ISocialSecurityBenefit[] = [
              {
                personId: persons[0].id,
                fullBenefit: fullBenefit,
                personalStart: benefit1.myStart ? getISODateFromDate(benefit1.myStart) : undefined,
                personalBenefit: benefit1.myStart ? benefit1.myBenefit : 0,
                personalPercent: benefit1.myStart ? benefit1.myPercent : 0,
                spousalStart: benefit1.spousalStart ? getISODateFromDate(benefit1.spousalStart) : undefined,
                spousalBenefit: benefit1.spousalStart ? benefit1.spousalBenefit : 0,
                spousalPercent: benefit1.spousalStart ? benefit1.spousalPercent : 0,
                annualInf: infRate
              },
              {
                personId: persons[1].id,
                fullBenefit: spouseFullBenefit ?? 0,
                personalStart: benefit2.myStart ? getISODateFromDate(benefit2.myStart) : undefined,
                personalBenefit: benefit2.myStart ? benefit2.myBenefit : 0,
                personalPercent: benefit2.myStart ? benefit2.myPercent : 0,
                spousalStart: benefit2.spousalStart ? getISODateFromDate(benefit2.spousalStart) : undefined,
                spousalBenefit: benefit2.spousalStart ? benefit2.spousalBenefit : 0,
                spousalPercent: benefit2.spousalStart ? benefit2.spousalPercent : 0,
                annualInf: infRate
              }
            ]
            let lifetimeBenefits = 0
            if (netWorthCalc) {
              lifetimeBenefits = netWorthCalc(ssBenefits)
            } else {
              lifetimeBenefits = this.calculateLifetimeBenefits(ssBenefits, persons)
            }
            // console.log(`${year}+${month % 12}mos | ${year2}+${month2 % 12}mos: ${numberToMoneyFormat(lifetimeBenefits, 0)}`)
            if (lifetimeBenefits > maxLifetimeBenefits || benefits.length === 0) {
              maxLifetimeBenefits = lifetimeBenefits
              benefits = ssBenefits
              maxMonth1 = month
              maxMonth2 = month2
            }
            ++count
          }
        } else {
          const benefit = this.calculateRetirementBenefits(birthDate, startDate, fullBenefit)
          const ssBenefit: ISocialSecurityBenefit = {
            personId: persons[0].id,
            fullBenefit: fullBenefit,
            personalStart: getISODateFromDate(startDate),
            personalBenefit: benefit.myBenefit,
            personalPercent: benefit.myPercent,
            spousalBenefit: 0,
            annualInf: infRate
          }
          let lifetimeBenefits = 0
          if (netWorthCalc) {
            lifetimeBenefits = netWorthCalc([ssBenefit])
          } else {
            lifetimeBenefits = this.calculateLifetimeBenefits([ssBenefit], persons)
          }
          // console.log(`${year}+${month % 12}mos: ${numberToMoneyFormat(lifetimeBenefits, 0)}`)
          if (lifetimeBenefits > maxLifetimeBenefits || benefits.length === 0) {
            maxLifetimeBenefits = lifetimeBenefits
            benefits = [ssBenefit]
            maxMonth1 = month
          }
        }
        ++count
      }

      if (netWorthCalc) {
        if (pass === 0) {
          // Iterate through the +/-12 months around the max year 3 months at a time
          monthIncr = 3
          monthRange1 = [Math.max(maxMonth1 - 12, monthRange1[0]), Math.min(maxMonth1 + 12, monthRange1[1])]
          // console.debug(`yearRange1 = [${yearRange1[0]}, ${yearRange1[1]}]`)
          if (birthDate2) {
            monthRange2 = [Math.max(maxMonth2 - 12, monthRange2[0]), Math.min(maxMonth2 + 12, monthRange2[1])]
            // console.debug(`yearRange2 = [${yearRange2[0]}, ${yearRange2[1]}]`)
          }
        } else if (pass === 1) {
          // Iterate through the +/-3 months around the max quarter
          monthIncr = 1
          monthRange1 = [Math.max(maxMonth1 - 2, monthRange1[0]), Math.min(maxMonth1 + 2, monthRange1[1])]
          // console.debug(`yearRange1 = [${yearRange1[0]}, ${yearRange1[1]}]`)
          if (birthDate2) {
            monthRange2 = [Math.max(maxMonth2 - 2, monthRange2[0]), Math.min(maxMonth2 + 2, monthRange2[1])]
            // console.debug(`yearRange2 = [${yearRange2[0]}, ${yearRange2[1]}]`)
          }
        }
      }
    }

    return benefits
  }

  static calculateLifetimeBenefits(benefits: ISocialSecurityBenefit[], persons: Person[]): number {

    if (persons.length > 0 && benefits.length > 0) {
      const dates = []
      const person1 = persons[0]
      const benefits1 = benefits[0]
      if (benefits1.personalStart) {
        dates.push(benefits1.personalStart)
      }
      if (benefits1.spousalStart) {
        dates.push(benefits1.spousalStart)
      }
      let person2 = persons.length > 1 ? persons[1] : undefined
      let benefits2 = benefits.length > 1 ? benefits[1] : undefined
      if (benefits2) {
        if (benefits2.personalStart) {
          dates.push(benefits2.personalStart)
        }
        if (benefits2.spousalStart) {
          dates.push(benefits2.spousalStart)
        }
      }

      const minIsoDate = dates.reduce((accum: string, date: string) => accum.localeCompare(date) <= 0 ? accum : date, "2999-12-31")
      const today = startOfToday()
      let minDate = isoToLocalDate(minIsoDate)
      if (minDate.getFullYear() < today.getFullYear()) {
        // Don't start in the past
        minDate = startOfYear(today)
      }
      minDate = addYears(minDate, -1)
      let totalBenefit1 = 0
      let totalBenefit2 = 0

      const dataSeries: { name: string, color: string, data: any[] }[] = []
      let data: [number, number, number?][] = []

      // A personal benefit period only exists if it begins before the spousal benefit
      if (benefits1.personalStart && (!benefits1.spousalStart || benefits1.personalStart < benefits1.spousalStart)) {
        data.push(this.getDataValue(benefits1.personalStart, 0, benefits1.annualInf))
        if (benefits1.spousalStart) {
          const personalData = this.getDateRangeValues(benefits1.personalStart, this.priorMonth(benefits1.spousalStart), benefits1.personalBenefit, benefits1.annualInf)
          let spousalData: [number, number, number][] = []
          let survivorData: [number, number, number][] = []
          if (person2 && person1.lifeExpectancyDate > person2.lifeExpectancyDate && benefits2 && benefits2.personalBenefit > benefits1.personalBenefit) {
            // Take the spouses greater benefit if live longer
            spousalData = this.getDateRangeValues(benefits1.spousalStart, person2.lifeExpectancyDate, benefits1.spousalBenefit, benefits1.annualInf)
            survivorData = this.getDateRangeValues(this.nextDay(person2.lifeExpectancyDate), person1.lifeExpectancyDate, benefits2.personalBenefit, benefits2.annualInf)
          } else {
            spousalData = this.getDateRangeValues(benefits1.spousalStart, person1.lifeExpectancyDate, benefits1.spousalBenefit, benefits1.annualInf)
          }
          data = [...data, ...personalData, ...spousalData, ...survivorData]
        } else {
          if (person2 && person1.lifeExpectancyDate > person2.lifeExpectancyDate && benefits2 && benefits2.personalBenefit > benefits1.personalBenefit) {
            // Take the spouses greater benefit if live longer
            const personalData = this.getDateRangeValues(benefits1.personalStart, person2.lifeExpectancyDate, benefits1.personalBenefit, benefits1.annualInf)
            const spouseData = this.getDateRangeValues(this.nextDay(person2.lifeExpectancyDate), person1.lifeExpectancyDate, benefits2.personalBenefit, benefits2.annualInf)
            data = [...data, ...personalData, ...spouseData]
          } else {
            const personalData = this.getDateRangeValues(benefits1.personalStart, person1.lifeExpectancyDate, benefits1.personalBenefit, benefits1.annualInf)
            data = [...data, ...personalData]
          }
        }
      } else if (benefits1.spousalStart) {
        data.push(this.getDataValue(benefits1.spousalStart, 0))
        let spousalData: [number, number, number][] = []
        let survivorData: [number, number, number][] = []
        if (person2 && person1.lifeExpectancyDate > person2.lifeExpectancyDate && benefits2 && benefits2.personalBenefit > benefits1.personalBenefit) {
          // Take the spouses greater benefit if live longer
          spousalData = this.getDateRangeValues(benefits1.spousalStart, person2.lifeExpectancyDate, benefits1.spousalBenefit, benefits1.annualInf)
          survivorData = this.getDateRangeValues(this.nextDay(person2.lifeExpectancyDate), person1.lifeExpectancyDate, benefits2.personalBenefit, benefits2.annualInf)
        } else {
          spousalData = this.getDateRangeValues(benefits1.spousalStart, person1.lifeExpectancyDate, benefits1.spousalBenefit, benefits1.annualInf)
        }
        data = [...data, ...spousalData, ...survivorData]
      }
      data.push(this.getDataValue(person1.lifeExpectancyDate, 0))
      dataSeries.push({name: `${person1.nickname}'s Social Security Benefit`, color: globalColors.primary, data: data})
      totalBenefit1 = data.reduce((accum: number, datum: [number, number, number?]) => datum[2] ? accum + datum[2] : accum, 0)
      // console.debug(`${person1.nickname}'s Total Benefit: ${numberToMoneyFormat(totalBenefit1, 0)}`)

      if (benefits2 && person2) {
        data = []
        // A personal benefit period only exists if it begins before the spousal benefit
        if (benefits2.personalStart && (!benefits2.spousalStart || benefits2.personalStart < benefits2.spousalStart)) {
          data.push(this.getDataValue(benefits2.personalStart, 0))
          if (benefits2.spousalStart) {
            const personalData = this.getDateRangeValues(benefits2.personalStart, this.priorMonth(benefits2.spousalStart), benefits2.personalBenefit, benefits2.annualInf)
            let spousalData: [number, number, number][] = []
            let survivorData: [number, number, number][] = []
            if (person2.lifeExpectancyDate > person1.lifeExpectancyDate && benefits1.personalBenefit > benefits2.personalBenefit) {
              // Take the spouses greater benefit if live longer
              spousalData = this.getDateRangeValues(benefits2.spousalStart, person1.lifeExpectancyDate, benefits2.spousalBenefit, benefits2.annualInf)
              survivorData = this.getDateRangeValues(this.nextDay(person1.lifeExpectancyDate), person2.lifeExpectancyDate, benefits1.personalBenefit, benefits1.annualInf)
            } else {
              spousalData = this.getDateRangeValues(benefits2.spousalStart, person2.lifeExpectancyDate, benefits2.spousalBenefit, benefits2.annualInf)
            }
            data = [...data, ...personalData, ...spousalData, ...survivorData]
          } else {
            if (person2.lifeExpectancyDate > person1.lifeExpectancyDate && benefits1.personalBenefit > benefits2.personalBenefit) {
              // Take the spouses greater benefit if live longer
              const personalData = this.getDateRangeValues(benefits2.personalStart, person1.lifeExpectancyDate, benefits2.personalBenefit, benefits2.annualInf)
              const spouseData = this.getDateRangeValues(this.nextDay(person1.lifeExpectancyDate), person2.lifeExpectancyDate, benefits1.personalBenefit, benefits1.annualInf)
              data = [...data, ...personalData, ...spouseData]
            } else {
              const personalData = this.getDateRangeValues(benefits2.personalStart, person2.lifeExpectancyDate, benefits2.personalBenefit, benefits2.annualInf)
              data = [...data, ...personalData]
            }
          }
        } else if (benefits2.spousalStart) {
          data.push(this.getDataValue(benefits2.spousalStart, 0))
          let spousalData: [number, number, number][] = []
          let survivorData: [number, number, number][] = []
          if (person2.lifeExpectancyDate > person1.lifeExpectancyDate && benefits1.personalBenefit > benefits2.personalBenefit) {
            // Take the spouses greater benefit if live longer
            spousalData = this.getDateRangeValues(benefits2.spousalStart, person1.lifeExpectancyDate, benefits2.spousalBenefit, benefits2.annualInf)
            survivorData = this.getDateRangeValues(this.nextDay(person1.lifeExpectancyDate), person2.lifeExpectancyDate, benefits1.personalBenefit, benefits1.annualInf)
          } else {
            spousalData = this.getDateRangeValues(benefits2.spousalStart, person2.lifeExpectancyDate, benefits2.spousalBenefit, benefits2.annualInf)
          }
          data = [...data, ...spousalData, ...survivorData]
        }
        data.push(this.getDataValue(person2.lifeExpectancyDate, 0))
        dataSeries.push({
          name: `${person2.nickname}'s Social Security Benefit`,
          color: globalColors.secondary,
          data: data
        })
        totalBenefit2 = data.reduce((accum: number, datum: [number, number, number?]) => datum[2] ? accum + datum[2] : accum, 0)
        // console.debug(`${person2.nickname}'s Total Benefit: ${numberToMoneyFormat(totalBenefit2, 0)}`)
      }

      return (totalBenefit1 + totalBenefit2)
    } else {
      return 0
    }
  }

  static nextDay(isoDate: string) {
    const date = addDays(isoToLocalDate(isoDate), 1)
    return getISODateFromDate(date)
  }

  static priorMonth(isoDate: string) {
    const date = endOfMonth(addMonths(isoToLocalDate(isoDate), -1))
    return getISODateFromDate(date)
  }

  static getDataValue(isoDate: string, presentValue: number, infRate: number = 0) : [number, number, number] {
    let futureValue = 0
    let date = isoToLocalDate(isoDate)
    const today = getDateToday()
    if (date.getFullYear() < today.getFullYear()) {
      // Move start up to start of current year if it is in the past
      date = startOfYear(today)
    }

    if (presentValue > 0) {
      const months = differenceInMonths(date, today)
      const rate = infRate / 12
      futureValue = this.futureValue(presentValue, rate, months)
    }
    return [date.getTime(), futureValue, 0]
  }

  static getDateRangeValues(start: string, end: string, presentValue: number, infRate: number = 0) : [number, number, number][] {
      // console.debug(`${start} - ${end}: ${numberToMoneyFormat(presentValue, 0)}`)
      const values: [number, number, number][] = []
      const today = getDateToday()
      let date = isoToLocalDate(start)
      if (date.getFullYear() < today.getFullYear()) {
        // Move start up to start of current year if it is in the past
        date = startOfYear(today)
      }
      const endDate = isoToLocalDate(end)

      // Get value at start of range
      let thisYear = today.getFullYear()
      let startYear = date.getFullYear()
      let value = presentValue
      // let years = differenceInCalendarYears(date, today)
      let year: number
      for (year = thisYear + 1; year <= startYear; year++) {
        value += value * infRate
      }
      values.push([date.getTime(), value, 0])

      // Compute yearly intermediate points
      while (date.getFullYear() < endDate.getFullYear()) {
        let nextDate = startOfYear(addYears(date, 1))
        let months = differenceInCalendarMonths(nextDate, date)
        let benefit = Math.round(months * value)
        // console.debug(`${date.getFullYear()}: ${numberToMoneyFormat(value, 0)} x ${months}mos = ${numberToMoneyFormat(benefit, 0)}`)
        values.push([nextDate.getTime(), value, benefit])
        // Get start of next year and new value
        // nextDate = startOfDay(addDays(nextDate, 1))
        ++year
        value += value * infRate
        values.push([nextDate.getTime(), value, 0])
        date = nextDate
      }

      // Handle remaining months
      const months = differenceInCalendarMonths(endDate, date) + 1
      if (months > 0) {
        let benefit = months * value
        // console.debug(`${endDate.getFullYear()}: ${numberToMoneyFormat(value, 0)} x ${months}mos = ${numberToMoneyFormat(benefit, 0)}`)
        values.push([endDate.getTime(), value, benefit])
      }

      return values
    }

  static futureValue(presentValue: number, infRate: number, periods: number): number {
    if (infRate === 0) {
      return presentValue
    } else {
      const futureValue = presentValue * Math.pow(1.0 + infRate, periods)
      return roundMoney(futureValue)
    }
  }
}

export default SocialSecurityCalc